import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import WalletCashIcon from '@/assets/icons/wallet-cash';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { ClaimFunnelEvents } from '@/feature/analytics/sbk/claim-funnel-events';
import { useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { useClaimFunnelStore } from '@/hooks/use-claim-funnel-store';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { SocialCurrency, useSocialCurrencyStore } from '@/hooks/use-social-currency';
import { logger } from '@/utils/logging';
import { toSocialCurrency } from '@/utils/numeric/currency';

import { useClaimTopUp, useTopUp } from './use-top-up';

export const isCrossProductClaimPending = () => useBetslipStore.getState().hasClaimedTopUpBonus;

/**
 * On the Sweeps lobby, if the user just claimed the top-up bonus from the
 * Picks betslip, POST the claim and present the success sheet.
 */
export const useCrossProductClaimFlow = () => {
    const { t } = useTranslation(['wallet']);
    const hasSocialWallet = useHasSocialWallet();
    const hasClaimedTopUpBonus = useBetslipStore(state => state.hasClaimedTopUpBonus);
    const setHasClaimedTopUpBonus = useBetslipStore(state => state.actions.setHasClaimedTopUpBonus);
    const { data, isFetching } = useTopUp();
    const { mutate: claimTopUp } = useClaimTopUp();
    const { showInfoSheet } = useAlerts();
    const setActiveCurrency = useSocialCurrencyStore(state => state.actions.setActiveCurrency);
    const setCreditAwaitingUse = useClaimFunnelStore(state => state.actions.setCreditAwaitingUse);
    const claimInFlightRef = useRef(false);

    useEffect(() => {
        if (!hasSocialWallet || !hasClaimedTopUpBonus || isFetching || !data || claimInFlightRef.current) {
            return;
        }
        claimInFlightRef.current = true;
        ClaimFunnelEvents.sweepsLanded();
        claimTopUp('crossover_top_up', {
            onSuccess: response => {
                // BE returns 200 with 0 amounts when eligibility lapsed (shared daily/crossover cooldown) - nothing credited.
                const amount = response.data.cash_top_up_amount;
                if (amount <= 0) {
                    claimInFlightRef.current = false;
                    setHasClaimedTopUpBonus(false);
                    ClaimFunnelEvents.claimResult('failure', 'ineligible');
                    return;
                }
                ClaimFunnelEvents.claimResult('success');
                ClaimFunnelEvents.walletCredited();
                setCreditAwaitingUse(true);
                const selectCurrency = (currency: 'betr_cash' | 'gold_coins', socialCurrency: SocialCurrency) => () => {
                    ClaimFunnelEvents.currencySelected(currency);
                    setActiveCurrency(socialCurrency);
                };
                showInfoSheet({
                    icon: <WalletCashIcon size={80} />,
                    title: t('wallet:crossover_claim_title', { amount: toSocialCurrency(amount) }),
                    description: t('wallet:crossover_claim_description'),
                    buttonLabel: t('wallet:play_with_betr_cash'),
                    secondaryLabel: t('wallet:play_with_gold_coins'),
                    handlePress: selectCurrency('betr_cash', SocialCurrency.CASH),
                    handleSecondaryPress: selectCurrency('gold_coins', SocialCurrency.COINS),
                    onDismiss: () => {
                        claimInFlightRef.current = false;
                        setHasClaimedTopUpBonus(false);
                    },
                });
                ClaimFunnelEvents.modalShown();
            },
            onError: (error: Error) => {
                claimInFlightRef.current = false;
                ClaimFunnelEvents.claimResult('failure', error.message);
                logger.debug('[Crossover claim]', 'Error claiming crossover top up:', error);
            },
        });
    }, [
        hasSocialWallet,
        hasClaimedTopUpBonus,
        isFetching,
        setHasClaimedTopUpBonus,
        data,
        claimTopUp,
        showInfoSheet,
        setActiveCurrency,
        setCreditAwaitingUse,
        t,
    ]);
};
